import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { api } from "../api/client";
import { useMutation } from "../hooks/useResource";
import { ErrorBanner } from "./common";

const MIN_LENGTH = 12;

/**
 * Self-service password change. The server revokes every session on success —
 * including this one — so the dialog signs the operator out rather than leaving a
 * console whose next request will 401.
 */
export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const mutation = useMutation(async () => {
    await api.post("/api/auth/password", { currentPassword: current, newPassword: next });
  });

  const mismatch = confirm !== "" && confirm !== next;
  const tooShort = next !== "" && next.length < MIN_LENGTH;
  const submittable = current !== "" && next.length >= MIN_LENGTH && confirm === next && !mutation.pending;

  const submit = async () => {
    if (!(await mutation.run(undefined))) return;
    onClose();
    navigate("/login", { replace: true });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change password</DialogTitle>
      <DialogContent>
        <ErrorBanner error={mutation.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          Changing your password signs out every session, including this one.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="Current password"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            fullWidth
          />
          <TextField
            label="New password"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            error={tooShort}
            helperText={`At least ${MIN_LENGTH} characters. A long passphrase beats a short complicated one.`}
            fullWidth
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            error={mismatch}
            helperText={mismatch ? "Does not match." : " "}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.pending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!submittable}>
          Change password
        </Button>
      </DialogActions>
    </Dialog>
  );
}
